import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { useGlobalQuery } from 'state/query-client/hooks';
import { useConfig } from 'hooks/use-config';
import { getUserById } from 'features/profile/services/accounts-service';
import { agentService } from 'features/chat/agent-chat-module/services/agent.service';
import { getSubscriptionUsage } from 'features/subscription/services/subscription-service';
import { DEFAULT_CHAT_AGENT_ID } from '../utils/usage-format';
import {
  GetTokenUsageParams,
  getTokenUsage,
  queryTokenUsageByOperation,
} from '../services/usage-service';

function useTokenUsageParams(params: GetTokenUsageParams) {
  return useMemo(
    () => ({
      startDate: params.startDate,
      endDate: params.endDate,
      scope: params.scope,
      userId: params.userId,
    }),
    [params.endDate, params.startDate, params.scope, params.userId]
  );
}

export const useTokenUsage = (params: GetTokenUsageParams, enabled = true) => {
  const { configurationSettings } = useConfig();
  const memoizedParams = useTokenUsageParams(params);

  return useGlobalQuery({
    queryKey: ['token-usage', memoizedParams],
    queryFn: () => getTokenUsage(configurationSettings, memoizedParams),
    enabled: enabled && (params.scope !== 'user' || !!params.userId),
    staleTime: 0,
    refetchOnMount: true,
  });
};

export const useSubscriptionUsage = (enabled = true) => {
  const { configurationSettings } = useConfig();

  return useGlobalQuery({
    queryKey: ['subscription-usage-current'],
    queryFn: () => getSubscriptionUsage(configurationSettings),
    enabled: enabled && !!configurationSettings,
    staleTime: 0,
    refetchOnMount: true,
  });
};

export const useUsageUserNames = (userIds: string[]) => {
  const { configurationSettings } = useConfig();
  const results = useQueries({
    queries: userIds.map((userId) => ({
      queryKey: ['getUserById', userId],
      queryFn: () => getUserById(userId, configurationSettings),
      enabled: !!userId,
    })),
  });

  return useMemo(() => {
    const names: Record<string, string> = {};
    results.forEach((result, index) => {
      const user = result.data;
      if (!user) return;
      const name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
      names[userIds[index]] = name || user.email;
    });
    return names;
  }, [results, userIds]);
};

export const useUsageAgentNames = (agentIds: string[]) => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';
  const catalogueIds = agentIds.filter((agentId) => agentId && agentId !== DEFAULT_CHAT_AGENT_ID);

  const results = useQueries({
    queries: catalogueIds.map((agentId) => ({
      queryKey: ['agent', { id: agentId, projectKey: blocksKey }],
      queryFn: () => agentService.getAgentById({ id: agentId, projectKey: blocksKey }, blocksKey),
      enabled: !!blocksKey && !!agentId,
    })),
  });

  return useMemo(() => {
    const names: Record<string, string> = {};
    results.forEach((result, index) => {
      const name = result.data?.agent?.name;
      if (name) names[catalogueIds[index]] = name;
    });
    return names;
  }, [results, catalogueIds]);
};

export const useTokenUsageByOperation = (params: GetTokenUsageParams, enabled = true) => {
  const { configurationSettings } = useConfig();
  const memoizedParams = useTokenUsageParams(params);

  return useGlobalQuery({
    queryKey: ['token-usage-by-operation', memoizedParams],
    queryFn: () => queryTokenUsageByOperation(configurationSettings, memoizedParams),
    enabled: enabled && (params.scope !== 'user' || !!params.userId),
    staleTime: 0,
    refetchOnMount: true,
  });
};
